import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ImageStyle, KeyboardAvoidingView, Platform, ScrollView, StyleSheet, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { connect, useSelector } from 'react-redux';
import { AUTH_MODE } from '../../constants/Constants';
import { LanguageType } from '../../constants/Languages';
import theme from '../../constants/theme';
import { SCREEN_WIDTH } from '../../utils/screensize';
import { RootState } from '../../store/reducers';
import ConfirmModal, { ModalConfig } from '../global/ConfirmModal';
import Text from '../global/Text';
import Auth from './Auth';
import LandingHeader from './LandingHeader';
import LandingLanguageModal from './LandingLanguageModal';
import LandingLoading from './LandingLoading';
import LandingVersion from './LandingVersion';
import ModalNotifRequestAccess from './utils/ModalNotifRequestAccess';
import fontMaker from '../../utils/font';

const IS_WIDE = SCREEN_WIDTH >= 768;
const IS_LARGE_DESKTOP = SCREEN_WIDTH >= 1280;
const BRANDING_FEATURE_KEYS = [
  'auth:branding.featureRealtimeInsights',
  'auth:branding.featureAuditManagement',
  'auth:branding.featureCollaborativeWorkflows'
];
// eslint-disable-next-line @typescript-eslint/no-var-requires
const logoNimblyAsset = require('../../assets/img/logo-nimbly.png');
const logoNimblyUrl = logoNimblyAsset.default || logoNimblyAsset;

const logoIconStyle: ImageStyle = {
  width: 72,
  height: 72,
  resizeMode: 'contain'
};

export type LandingContainerProps = {
  /** True if component is still loading (waiting for auth and profile) */
  _isLoading: boolean;
  /** True if loading timed out */
  _isTimedOut: boolean;
  /** True to show concurrent login modal */
  _showModal: boolean;
  /** Config for the concurrent login modal */
  _modalConfig: ModalConfig;
  /** Language to be used throughout the app */
  language: LanguageType;
  /** The redux Firebase auth object */
  auth: any;
  /** The redux Firebase user profile object */
  profile: any;
  /** Database version for check and forced update (e.g. '1.20.0') */
  latestVersion: string;
  /** uuid of the device */
  uuid: string;
  /** True if the device has notches (e.g. iPhone X) */
  hasNotch: boolean;
  /** True when Firebase still has a valid authenticated user session */
  hasActiveSession: boolean;
  /** True when landing should show standalone fullscreen loading instead of auth layout */
  showFullscreenLoading: boolean;
  /** Event - language switcher is clicked */
  onLanguageChange: (language: LanguageType) => void;
  /** Event - modal is dismissed */
  onDismissModal: () => void;
  /** Get - color of the profile organization */
  profileColor: string;
};

const LandingContainer = (props: LandingContainerProps) => {
  const { t } = useTranslation(['auth']);
  const scrollRef: React.RefObject<ScrollView> = React.createRef();
  const [showModalRequestAccess, setShowModalRequestAccess] = useState<boolean>(false);
  const authMode = useSelector<RootState, AUTH_MODE>(state => state.landing.authMode);
  const shouldShowAuthForm =
    !props.hasActiveSession &&
    (Object.values(AUTH_MODE).includes(authMode) ||
      props._isTimedOut ||
      (!props._isLoading && props.auth.isLoaded && props.auth.isEmpty));

  const handleScrollDown = () => {
    if (scrollRef && scrollRef?.current) {
      scrollRef?.current!.scrollToEnd({ animated: true });
    }
  };

  useEffect(() => {
    if (showModalRequestAccess) {
      setTimeout(() => {
        setShowModalRequestAccess(false);
      }, 2500);
    }
  }, [showModalRequestAccess]);

  const brandingColor = props.profileColor || theme.colors.primary;

  if (props.showFullscreenLoading) {
    return (
      <View style={styles.fullscreenLoadingRoot}>
        <LandingLanguageModal onLanguageChange={props.onLanguageChange} />
        <ConfirmModal
          isVisible={props._showModal}
          language={props.language}
          title={props._modalConfig.title}
          subtitle={props._modalConfig.subtitle}
          options={props._modalConfig.options}
          onCloseModal={props.onDismissModal}
        />
        <LandingLoading language={props.language} fullscreen />
      </View>
    );
  }

  return (
    <View style={[styles.landingRoot, props.hasNotch ? styles.landingNotch : null]}>
      <LandingLanguageModal onLanguageChange={props.onLanguageChange} />
      <ConfirmModal
        isVisible={props._showModal}
        language={props.language}
        title={props._modalConfig.title}
        subtitle={props._modalConfig.subtitle}
        options={props._modalConfig.options}
        onCloseModal={props.onDismissModal}
      />

      {IS_WIDE && (
        <View style={[styles.leftPanel, { backgroundColor: brandingColor }]}>
          <View style={styles.leftPanelContent}>
            <View style={styles.brandLogoFrame}>
              <Image source={logoNimblyUrl} style={logoIconStyle} />
              <Text style={styles.brandLogoText}>{'Nimbly\nTechnologies'}</Text>
            </View>
            <Text style={styles.brandTitle}>{t('auth:branding.title')}</Text>
            <Text style={styles.brandSubtitle}>{t('auth:branding.subtitle')}</Text>

            <View style={styles.featureList}>
              {BRANDING_FEATURE_KEYS.map(featureKey => (
                <View key={featureKey} style={styles.featureRow}>
                  <View style={styles.featureIconWrap}>
                    <Icon name="check" size={18} color="#FFFFFF" />
                  </View>
                  <Text style={styles.featureItem}>{t(featureKey)}</Text>
                </View>
              ))}
            </View>
          </View>
          <LandingVersion />
        </View>
      )}

      <View style={styles.rightPanel}>
        <LandingHeader language={props.language} onLanguageChange={props.onLanguageChange} />
        <KeyboardAvoidingView
          style={styles.rightPanelInner}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          enabled={true}
        >
          <ScrollView
            ref={scrollRef}
            style={styles.landingScroll}
            contentContainerStyle={styles.landingContainer}
            keyboardShouldPersistTaps="always"
          >
            <ModalNotifRequestAccess
              isVisible={showModalRequestAccess}
              onCloseModalRequest={() => setShowModalRequestAccess(false)}
            />
            <View style={styles.authShell}>
              {shouldShowAuthForm ? (
                <Auth setShowModalRequestAccess={setShowModalRequestAccess} onScrollDown={handleScrollDown} />
              ) : (
                <LandingLoading language={props.language} />
              )}
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </View>
  );
};

const mapStateToProps = (state: RootState) => ({
  profileColor: state.profiletheme.color
});

const styles = StyleSheet.create({
  fullscreenLoadingRoot: {
    flex: 1,
    minHeight: '100vh',
    backgroundColor: '#F5F7FA',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16
  },
  landingRoot: {
    flex: 1,
    flexDirection: IS_WIDE ? 'row' : 'column',
    position: 'fixed' as any,
    margin: 0,
    padding: 0,
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
    overflow: 'hidden'
  },
  landingNotch: {
    paddingTop: 24,
    paddingBottom: 12
  },
  leftPanel: {
    flex: 0.45,
    justifyContent: 'center',
    paddingHorizontal: IS_LARGE_DESKTOP ? 64 : 56,
    paddingTop: IS_LARGE_DESKTOP ? 72 : 56
  },
  leftPanelContent: {
    width: '100%',
    maxWidth: 520,
    paddingLeft: 6
  },
  brandLogoFrame: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26
  },
  brandLogoText: {
    ...fontMaker({ weight: 'Bold' }),
    color: '#ffffff',
    fontSize: 22,
    lineHeight: 28,
    marginLeft: 12
  },
  featureList: {
    marginTop: 34,
    width: '100%',
    maxWidth: 460
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 30
  },
  featureIconWrap: {
    width: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16
  },
  featureItem: {
    ...fontMaker({ weight: 'Regular' }),
    flex: 1,
    color: 'rgba(255,255,255,0.96)',
    fontSize: 17,
    lineHeight: 26
  },
  brandTitle: {
    ...fontMaker({ weight: 'Bold' }),
    color: '#fff',
    fontSize: IS_LARGE_DESKTOP ? 54 : 46,
    lineHeight: IS_LARGE_DESKTOP ? 60 : 52,
    letterSpacing: -0.8,
    marginBottom: 22,
    maxWidth: 500
  },
  brandSubtitle: {
    ...fontMaker({ weight: 'Regular' }),
    color: 'rgba(255,255,255,0.82)',
    fontSize: 18,
    lineHeight: 32,
    maxWidth: 470
  },
  rightPanel: {
    flex: IS_WIDE ? 0.55 : 1,
    backgroundColor: '#F5F7FA',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  rightPanelInner: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center'
  },
  landingScroll: {
    flex: 1,
    width: '100%'
  },
  landingContainer: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: IS_WIDE ? 24 : 12,
    paddingBottom: 32,
    paddingHorizontal: 20
  },
  authShell: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

export default connect(mapStateToProps)(LandingContainer);
